import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    // Signature must match the existing declarations in ImageCompressorPage.tsx
    // and MotionControlPage.tsx — merged global interfaces must agree exactly.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Sends a GA4 page_view on client-side route changes.
 *
 * Why this exists (added 2026-09-05, after measuring it):
 *
 * gtag.js sends page_view once, from the `config` call on the initial document
 * load. In a single-page app every subsequent navigation is invisible to it
 * unless something sends the event explicitly — and nothing did. Measured on the
 * live site by clicking a homepage headline: after the route change, Google Ads
 * remarketing fired (viewthroughconversion + rmkt/collect + ccm/collect for
 * AW-16563492808), Ahrefs fired, Cloudflare Insights fired, and GA4 sent nothing
 * at all. GTM's History Change trigger is working; the GA4 tag simply is not
 * attached to it.
 *
 * The consequence was that GA4 counted entry pages, not page views. Pages-per-
 * session was ~1.0 by construction, and no internal navigation was measurable —
 * which is exactly the number needed to tell whether an onward-journey fix (nav
 * links, related articles, section footers) did anything.
 *
 * DO NOT also add a History Change trigger to the GA4 tag in the GTM container.
 * Either this or that, never both, or every navigation counts twice.
 *
 * The initial page_view is skipped here because `config` has already sent it.
 */
const PageViewTracker = () => {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  return null;
};

export default PageViewTracker;
